import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import { MapPin, Calendar, Wallet, Phone, MessageCircle, Check, Lock, Star, Rocket, Award } from "lucide-react";
import { toast } from "sonner";
import ReviewBox from "@/components/ReviewBox";
import PrivacyBanner from "@/components/PrivacyBanner";
import ShareWin from "@/components/ShareWin";

function StatusBadge({ s, t }) {
  const map = {
    pending: "bg-amber-50 text-amber-700",
    accepted: "bg-emerald-50 text-emerald-700",
    rejected: "bg-zinc-100 text-zinc-500",
  };
  return <span className={`pill ${map[s]}`}>{t(s)}</span>;
}

export default function RequestDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, tCat, tCity, tAct } = useLang();
  const [req, setReq] = useState(null);
  const [offers, setOffers] = useState([]);
  const [contact, setContact] = useState(null);
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/offers`),
      ]);
      setReq(r1.data);
      setOffers(r2.data);
      if (r1.data.merchant_contact) setContact(r1.data.merchant_contact);
      if (r1.data.accepted_offer_id) {
        try {
          const r3 = await api.get(`/requests/${id}/order`);
          if (r3.data) setOrder(r3.data);
        } catch (_e) { /* ignore */ }
      }
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const accept = async (offerId) => {
    try {
      const { data } = await api.post(`/offers/${offerId}/accept`);
      setContact(data.merchant_contact);
      setOrder(data.order);
      toast.success(t("offerAccepted"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <div className="app-shell p-5">…</div>;
  if (err) return <div className="app-shell p-5 text-red-600">{err}</div>;
  if (!req) return null;

  const isCustomer = user?.role === "customer";
  const acceptedOffer = offers.find((o) => o.status === "accepted");
  const whatsappLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;

  return (
    <div className="app-shell pb-24" data-testid="request-details">
      <TopBar title={t("requestDetails")} />
      <div className="px-5 pt-4">
        <div className="card-surface p-5">
          <div className="flex gap-3 items-start">
            <CategoryBadge category={req.category} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold">{tCat(req.category)}{req.activity ? ` · ${tAct(req.activity)}` : ""}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(req.city)}{req.district ? ` — ${req.district}` : ""}</span>
                <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{req.budget} {t("saudiRiyal")}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{req.deadline}</span>
                {req.expired && <span className="pill bg-zinc-200 text-zinc-500" data-testid="request-expired-pill">{t("expired")}</span>}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{req.description}</p>
          {req.other_detail && (
            <div className="mt-3 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-sm" data-testid="request-other-detail">
              <span className="block text-[11px] font-bold text-zinc-500 mb-0.5">{t("otherHomeQuestion")}</span>
              <span className="text-zinc-800">{req.other_detail}</span>
            </div>
          )}
          {req.service_method && (
            <p className="mt-2 text-xs text-zinc-500" data-testid="request-service-method">{t("serviceMethod")}: {tAct(req.service_method)}</p>
          )}
          {req.photo && <img src={req.photo} alt="" className="mt-4 w-full rounded-xl object-cover max-h-72" />}
        </div>

        {/* Accepted offer contact reveal (visible to customer post-accept and to winning merchant) */}
        {acceptedOffer && (contact || (!isCustomer && req.customer?.phone)) && (
          <div className="mt-5 card-surface p-5 border-emerald-200 bg-emerald-50" data-testid="contact-revealed">
            <p className="label-caps text-emerald-700">{isCustomer ? t("offerAccepted") : t("youWonOrder")}</p>
            {isCustomer && contact && (
              <div className="mt-2 space-y-2">
                <div className="font-semibold">{contact.business_name}</div>
                {contact.whatsapp && (
                  <a
                    href={whatsappLink(contact.whatsapp)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-btn"
                    className="btn-primary w-full"
                  >
                    <MessageCircle className="h-4 w-4" />{t("contactWhatsApp")}
                  </a>
                )}
                {contact.phone && (
                  <div className="text-sm text-zinc-700 inline-flex items-center gap-2"><Phone className="h-4 w-4" />{contact.phone}</div>
                )}
              </div>
            )}
            {!isCustomer && req.customer?.phone && (
              <div className="mt-2 space-y-1 text-sm">
                <div className="font-semibold">{req.customer.name}</div>
                <div className="inline-flex items-center gap-2"><Phone className="h-4 w-4" />{req.customer.phone}</div>
              </div>
            )}
          </div>
        )}

        {/* One-tap WhatsApp share — only customers, only after acceptance. */}
        {isCustomer && acceptedOffer && (
          <ShareWin
            category={req.category}
            createdAt={req.created_at}
            acceptedAt={(order && order.created_at) || acceptedOffer.created_at}
          />
        )}

        {/* Offers */}
        <div className="mt-6">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold">{isCustomer ? t("receivedOffers") : t("myOffers")}</h3>
            <span className="text-xs text-zinc-500">{offers.length}</span>
          </div>
          {isCustomer && req.status === "open" && (
            <div className="mt-3"><PrivacyBanner variant="customer" /></div>
          )}
          {offers.length === 0 && (
            <div className="mt-3 card-surface p-5 text-sm text-zinc-600">{t("noOffers")}</div>
          )}

          <div className="mt-3 space-y-3">
            {offers.map((o) => (
              <div key={o.id} className={`card-surface p-4 ${o.merchant?.featured ? "border-amber-300 ring-1 ring-amber-200" : o.boosted ? "border-emerald-300 ring-1 ring-emerald-200" : ""}`} data-testid={`offer-card-${o.id}`}>
                {o.merchant?.featured && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid={`featured-offer-${o.id}`}>
                    <Award className="h-3 w-3" /> {t("featured")}
                  </div>
                )}
                {!o.merchant?.featured && o.boosted && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid={`featured-${o.id}`}>
                    <Rocket className="h-3 w-3" /> {t("boosted")}
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to={`/m/${o.merchant?.id || o.merchant_id}`}
                    data-testid={`open-merchant-${o.id}`}
                    className="flex items-center gap-3 min-w-0 group"
                  >
                    {o.merchant?.logo ? (
                      <img src={o.merchant.logo} alt="" className="h-10 w-10 rounded-xl object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center font-bold">
                        {((o.merchant?.business_name || o.merchant_name) || "?")[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold truncate group-hover:underline underline-offset-4 decoration-emerald-500">
                        {o.merchant?.business_name || o.merchant_name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        {o.merchant?.city && <span>{tCity(o.merchant.city)}</span>}
                        {o.merchant?.rating != null && (
                          <span className="inline-flex items-center gap-0.5 text-amber-600" data-testid={`offer-rating-${o.id}`}>
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <b>{o.merchant.rating}</b>
                            <span className="text-zinc-400">({o.merchant.rating_count})</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  <StatusBadge s={o.status} t={t} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="label-caps">{t("price")}</div>
                    <div className="font-semibold text-emerald-700">{o.price} {t("saudiRiyal")}</div>
                  </div>
                  <div>
                    <div className="label-caps">{t("deliveryTime")}</div>
                    <div className="font-medium">{o.delivery_time}</div>
                  </div>
                </div>
                {o.notes && <p className="mt-3 text-sm text-zinc-700">{o.notes}</p>}
                {o.photo && <img src={o.photo} alt="" className="mt-3 w-full rounded-xl object-cover max-h-56" />}
                {isCustomer && req.status === "open" && (
                  <button
                    data-testid={`accept-offer-${o.id}`}
                    onClick={() => accept(o.id)}
                    className="btn-primary w-full mt-3"
                  >
                    <Check className="h-4 w-4" /> {t("acceptOffer")}
                  </button>
                )}
              </div>
            ))}
          </div>

          {isCustomer && req.status === "open" && offers.length > 0 && (
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <Lock className="h-3 w-3" /> {t("contactRevealed")}
            </p>
          )}

          {isCustomer && req.status === "completed" && order && (
            <div className="mt-6">
              <ReviewBox orderId={order.id} />
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
